import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Users, CheckCircle, Crown, Gift, AlertTriangle, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => Number(n).toFixed(2).replace(".", ",");

const PRO_FEATURES = [
  "Tutte le funzioni del piano Free",
  "Residui ferie e prospetti",
  "Report mensile per il consulente",
  "Logo e personalizzazione aspetto",
  "Notifiche via email",
  "Date bloccate e regole richieste",
  "Amministrativi e calendario privato",
];

const TIERS = [
  {
    key: "free",
    name: "Free",
    price: "Gratis",
    sub: "Da 0 a 5 dipendenti",
    icon: Gift,
    color: "emerald",
    features: [
      "Richieste ferie e permessi",
      "Approvazioni e notifiche in app",
      "Calendario condiviso",
      "Malattia e straordinari",
      "Per sempre, senza limiti di tempo",
    ],
  },
  {
    key: "pro_light",
    name: "Pro Light",
    price: "€4,90",
    sub: "al mese, fisso — da 0 a 5 dipendenti",
    icon: Sparkles,
    color: "amber",
    highlighted: true,
    features: PRO_FEATURES,
    note: "Dal 6° dipendente passi in automatico a Pro: ti avvisiamo noi.",
  },
  {
    key: "pro",
    name: "Pro",
    price: "€1,50",
    sub: "al mese per dipendente (da 6 a 65)",
    icon: Users,
    color: "blue",
    features: PRO_FEATURES,
  },
  {
    key: "enterprise",
    name: "Enterprise",
    price: "€99,00",
    sub: "al mese, fisso — oltre 65 dipendenti",
    icon: Crown,
    color: "purple",
    features: [
      "Tutte le funzioni del piano Pro",
      "Dipendenti illimitati",
      "Prezzo bloccato: non cresce più",
      "Supporto via email",
    ],
  },
];

const TIER_NAME = { pro_light: "Pro Light", pro: "Pro", enterprise: "Enterprise" };

export default function PricingV2Section({ status, onRefresh }) {
  const { token } = useAuth();
  const [paying, setPaying] = useState(false);
  const pollRef = useRef(false);

  const count = status.current_employees ?? 0;
  const tier = status.v2_tier || "free";
  const paid = status.payment_completed;
  const paidTier = status.v2_paid_tier || (tier === "free" ? "pro_light" : tier);
  const targetName = TIER_NAME[paidTier] || "Pro";

  // Handle return from Stripe checkout (?session_id=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId || pollRef.current) return;
    pollRef.current = true;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/api/stripe/checkout-status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.payment_status === "paid") {
          toast.success("Abbonamento attivato! Tutte le funzionalità sono ora sbloccate.");
          window.history.replaceState({}, "", "/abbonamento");
          onRefresh && onRefresh();
          return;
        }
      } catch (e) { /* retry */ }
      if (attempts < 6) setTimeout(poll, 2500);
    };
    poll();
  }, [token, onRefresh]);

  const handleSubscribe = async () => {
    setPaying(true);
    try {
      const res = await axios.post(
        `${API}/api/v2/checkout`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'avvio del pagamento");
      setPaying(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6" data-testid="pricing-v2-section">
      {/* Stato attuale */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="v2-current-status">
        <h2 className="text-lg font-bold text-slate-900 mb-1">Il tuo piano</h2>
        {paid ? (
          <p className="text-slate-600" data-testid="v2-current-plan-text">
            Piano <strong>{status.piano_nome || targetName}</strong> attivo — {count} dipendenti registrati.
            Canone attuale: <strong>€{fmt(status.monthly_price)}/mese</strong>
            {paidTier === "pro_light" && " (fino a 5 dipendenti; dal 6° passa in automatico a Pro, €1,50 per dipendente)"}
            {paidTier === "pro" && " (si aggiorna automaticamente al variare dei dipendenti)"}
          </p>
        ) : tier === "free" ? (
          <p className="text-slate-600" data-testid="v2-current-plan-text">
            Piano <strong>Free</strong> — <strong>{count}/5</strong> dipendenti registrati. Gratis per sempre fino a 5 dipendenti.
          </p>
        ) : (
          <p className="text-slate-600" data-testid="v2-current-plan-text">
            Hai <strong>{count} dipendenti</strong>: serve il piano <strong>{targetName}</strong> (€{fmt(status.monthly_price)}/mese).
          </p>
        )}
        {!paid && status.over_limit && (
          <div className="mt-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="v2-grace-banner">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              {status.is_blocked ? (
                <p><strong>Periodo di tolleranza terminato.</strong> Attiva il piano per riprendere a usare tutte le funzioni e permettere nuove registrazioni.</p>
              ) : (
                <p>Hai superato i 5 dipendenti del piano Free. Ti restano <strong>{status.grace_days_remaining} giorni</strong> per attivare il piano <strong>{targetName}</strong>.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Le 4 fasce progressive */}
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
        {TIERS.map((t) => {
          const Icon = t.icon;
          const isCurrent = paid ? paidTier === t.key : t.key === "free";
          return (
            <div
              key={t.key}
              className={`relative bg-white rounded-2xl border p-6 shadow-sm flex flex-col ${
                t.highlighted ? "border-amber-400 ring-2 ring-amber-100" : "border-slate-200"
              }`}
              data-testid={`v2-plan-${t.key}`}
            >
              {isCurrent && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  Piano attuale
                </span>
              )}
              <div className="flex items-center gap-2 mb-3">
                <div className={`p-2 rounded-lg bg-${t.color}-100`}>
                  <Icon className={`h-5 w-5 text-${t.color}-600`} />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{t.name}</h3>
              </div>
              <p className="text-3xl font-extrabold text-slate-900">{t.price}</p>
              <p className="text-sm text-slate-500 mb-4">{t.sub}</p>
              <ul className="space-y-2 text-sm text-slate-600 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
              {t.note && <p className="text-xs text-amber-700 mt-3" data-testid="v2-pro-light-note">{t.note}</p>}
              <p className="text-xs text-slate-400 mt-4">Attivazione gratuita · Nessun vincolo</p>
            </div>
          );
        })}
      </div>

      {/* CTA pagamento */}
      {!paid && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm text-center space-y-3" data-testid="v2-subscribe-card">
          {tier === "free" ? (
            <p className="text-slate-700">
              Vuoi tutte le funzioni Pro con il tuo team di <strong>{count} dipendenti</strong>?{" "}
              Con <strong>Pro Light</strong> paghi <strong>€4,90/mese</strong> fissi fino a 5 dipendenti.
            </p>
          ) : (
            <p className="text-slate-700">
              Con <strong>{count} dipendenti</strong> il tuo canone sarà di{" "}
              <strong>€{fmt(status.monthly_price)}/mese</strong>
              {tier === "pro" ? " (€1,50 × " + count + ")" : " (tariffa fissa Enterprise)"}
            </p>
          )}
          <Button
            onClick={handleSubscribe}
            disabled={paying}
            className={`${tier === "free" ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"} text-white px-8`}
            data-testid="v2-subscribe-btn"
          >
            {paying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Attiva il piano {targetName} — €{fmt(tier === "free" ? status.pro_light_price ?? 4.9 : status.monthly_price)}/mese
          </Button>
          <p className="text-xs text-slate-400">Pagamento sicuro con carta (Stripe). Disdici quando vuoi.</p>
        </div>
      )}
    </div>
  );
}
